import { CdkStep } from '@angular/cdk/stepper';
import {
    Component,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import {
    CaptchaAction,
    CaptchaError,
    CaptchaService,
    SwitchLanguageService
} from '../../../../core/services';
import { Language } from '../../../../domain/models';
import { AccountInfo } from '../../models';
import { AuthService, LocalAccountCreationService } from '../../services';
import { AccountSetupComponent } from '../account-setup/account-setup.component';
import { AccountStepperComponent } from '../account-stepper';
import { AccountVerificationComponent } from '../account-verification/account-verification.component';
import { AgeComponent } from '../age/age.component';
import { PreferredLanguageComponent } from '../preferred-language/preferred-language.component';

@Component({
    selector: 'app-account-creation',
    templateUrl: './account-creation.component.html',
    styleUrls: ['./account-creation.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslocoModule,
        AccountStepperComponent,
        CdkStep,
        AccountSetupComponent,
        AccountVerificationComponent,
        PreferredLanguageComponent,
        AgeComponent,
        FlexLayoutModule,
        MatButtonModule
    ]
})
export class AccountCreationComponent implements OnInit {
    @ViewChild(AccountStepperComponent) stepper:
        | AccountStepperComponent
        | undefined;

    /** True from the CONTINUE click on the account step until the code request settles. */
    codeRequestBusy = false;

    /** The CAPTCHA could not be completed, so no code was requested. */
    codeRequestFailed = false;

    constructor(
        private readonly _auth: AuthService,
        private readonly _localAccount: LocalAccountCreationService,
        private readonly _switchLanguage: SwitchLanguageService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private readonly _captcha: CaptchaService
    ) {}

    ngOnInit(): void {
        // Start from a clean slate. The stepper always opens on the first question, so any
        // values still held from an earlier attempt are stale and must not be able to combine
        // with what is entered this time.
        this._localAccount.clear();
        this._googleAnalyticsService.pageView('Sign up: Preferred language');
        // The code request on the account step needs the CAPTCHA; the language and age steps
        // give the script time to arrive before anyone clicks CONTINUE.
        this._captcha.preload(CaptchaAction.SendCreateAccountCode);
    }

    languageChosen(language: Language): void {
        this._localAccount.preferredLanguageId = language.languageId;
        this._switchLanguage.switch(
            language.languageCode.trimRight().split('-')[0],
            false
        );
        this._googleAnalyticsService.pageView('Sign up: How old are you');
        this.stepper?.next();
    }

    ageRangeChosen(ageRange: number): void {
        this._localAccount.ageRange = ageRange;
        this._googleAnalyticsService.pageView('Sign up: Setup your account');
        this.stepper?.next();
    }

    accountInfoSet(info: AccountInfo): void {
        // A second request would restart the challenge the first one is waiting on.
        if (this.codeRequestBusy) return;

        this._localAccount.firstName = info.firstName;
        this._localAccount.lastName = info.lastName;
        this._localAccount.emailOrPhoneNumber = info.emailOrPhoneNumber;
        this._localAccount.receivesNotifications = info.receivesNotifications;
        this._localAccount.acceptedPrivacyPolicy = info.acceptedPrivacyPolicy;
        this._googleAnalyticsService.pageView('Sign up: Verify your account');
        this.codeRequestBusy = true;
        this.codeRequestFailed = false;
        this._auth
            .requestCreateAccountCode(info.emailOrPhoneNumber)
            .pipe(
                tap(() => this.stepper?.next()),
                catchError((e) => {
                    // HTTP failures have already been reported by HttpErrorInterceptor.
                    if (e instanceof CaptchaError)
                        this.codeRequestFailed = true;
                    return EMPTY;
                }),
                finalize(() => (this.codeRequestBusy = false))
            )
            .subscribe();
    }

    codeProvided(code: number) {
        this._localAccount.securityCode = code;
        if (this._localAccount.complete) {
            this._auth
                .signUp(this._localAccount.createUserModel())
                .pipe(tap(() => this._localAccount.clear()))
                .subscribe();
        }
    }

    goBack(): void {
        this.stepper?.previous();
    }
}
